import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoint';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function PvPInvitationModal({ invitation, onAccept, onDecline, onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [timeRemaining, setTimeRemaining] = useState(invitation?.seconds_remaining || 120);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (!invitation) return;

        setTimeRemaining(invitation.seconds_remaining || 120);

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    onClose();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [invitation, onClose]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAccept = async () => {
        if (isProcessing) return;
        
        setIsProcessing(true);
        try {
            const response = await axios.post(API_ENDPOINTS.ACCEPT_PVP_INVITATION, {
                invitation_id: invitation.id
            });

            if (response.data && response.data.success) {
                toast.success(t('PVP invitation accepted! Starting battle...'));
                navigate(`/pvp?battle_id=${response.data.battle_id}&pvp=true`);
                if (onAccept) onAccept();
            } else {
                toast.error(response.data?.error || t('Failed to accept invitation'));
            }
        } catch (error) {
            console.error('Error accepting PVP invitation:', error);
            toast.error(error.response?.data?.error || t('Failed to accept invitation'));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDecline = async () => {
        if (isProcessing) return;
        
        setIsProcessing(true);
        try {
            const response = await axios.post(API_ENDPOINTS.DECLINE_PVP_INVITATION, {
                invitation_id: invitation.id
            });

            if (response.data && response.data.success) {
                toast.info(t('PVP invitation declined'));
                if (onDecline) onDecline();
            } else {
                toast.error(response.data?.error || t('Failed to decline invitation'));
            }
        } catch (error) {
            console.error('Error declining PVP invitation:', error);
            toast.error(error.response?.data?.error || t('Failed to decline invitation'));
        } finally {
            setIsProcessing(false);
        }
    };

    if (!invitation) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                borderRadius: '20px',
                padding: '40px',
                maxWidth: '450px',
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 195, 35, 0.2)',
                border: '3px solid #ffc323'
            }}>
                {/* Title */}
                <h2 style={{
                    color: '#ffc323',
                    fontSize: '28px',
                    fontWeight: 'bold',
                    marginBottom: '25px',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}>
                    ⚔️ {t('PVP Battle Invitation')}! ⚔️
                </h2>
                
                {/* Challenger Info */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '15px',
                    padding: '20px',
                    marginBottom: '25px'
                }}>
                    <p style={{
                        color: '#fff',
                        fontSize: '18px',
                        margin: '0 0 10px 0',
                        lineHeight: '1.5'
                    }}>
                        {t('challenges you to a battle')}
                    </p>
                    <p style={{
                        color: '#4CAF50',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        margin: '0',
                        textShadow: '1px 1px 3px rgba(0,0,0,0.5)'
                    }}>
                        {invitation.inviter_name}
                    </p>
                </div>
                
                {/* Timer */}
                <div style={{
                    backgroundColor: timeRemaining <= 30 ? 'rgba(244, 67, 54, 0.3)' : 'rgba(255, 195, 35, 0.2)',
                    borderRadius: '10px',
                    padding: '15px',
                    marginBottom: '30px',
                    border: `2px solid ${timeRemaining <= 30 ? '#f44336' : '#ffc323'}`
                }}>
                    <p style={{
                        color: '#fff',
                        fontSize: '14px',
                        margin: '0 0 5px 0',
                        opacity: 0.8
                    }}>
                        {t('Time remaining')}
                    </p>
                    <p style={{
                        color: timeRemaining <= 30 ? '#f44336' : '#ffc323',
                        fontSize: '36px',
                        fontWeight: 'bold',
                        margin: '0',
                        fontFamily: 'monospace',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                    }}>
                        {formatTime(timeRemaining)}
                    </p>
                </div>
                
                {/* Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '20px',
                    justifyContent: 'center'
                }}>
                    <button
                        onClick={handleAccept}
                        disabled={isProcessing || timeRemaining <= 0}
                        style={{
                            background: isProcessing || timeRemaining <= 0 
                                ? '#666' 
                                : 'linear-gradient(180deg, #4CAF50 0%, #2E7D32 100%)',
                            color: '#fff',
                            border: '2px solid #fff',
                            borderRadius: '30px',
                            padding: '15px 35px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: isProcessing || timeRemaining <= 0 ? 'not-allowed' : 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)'
                        }}
                        onMouseOver={(e) => {
                            if (!isProcessing && timeRemaining > 0) {
                                e.target.style.transform = 'scale(1.05)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'scale(1)';
                        }}
                    >
                        {isProcessing ? '...' : t('Accept')}
                    </button>
                    
                    <button
                        onClick={handleDecline}
                        disabled={isProcessing}
                        style={{
                            background: isProcessing 
                                ? '#666' 
                                : 'linear-gradient(180deg, #f44336 0%, #c62828 100%)',
                            color: '#fff',
                            border: '2px solid #fff',
                            borderRadius: '30px',
                            padding: '15px 35px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)'
                        }}
                        onMouseOver={(e) => {
                            if (!isProcessing) {
                                e.target.style.transform = 'scale(1.05)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'scale(1)';
                        }}
                    >
                        {isProcessing ? '...' : t('Decline')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PvPInvitationModal;
